import type { TaskTemplate } from "../../domain/entities.ts";
import { escapeHtml } from "../../ui/text.ts";
import { composeNoteTemplate, expandNoteTemplate, noteStarters, noteTemplateRecord } from "./note-templates.ts";

export interface NoteTemplateTarget {
  kind: "quick" | "reference";
  id: string;
  rootId: string | null;
  title: string;
  body: string;
  editable: boolean;
  start?: number;
  end?: number;
}
interface TemplateChoice { key: string; name: string; body: string; scope: string; record?: TaskTemplate }
interface Capabilities {
  document: Document;
  read(kind: NoteTemplateTarget["kind"]): NoteTemplateTarget | null;
  flush(target: NoteTemplateTarget): Promise<boolean>;
  write(target: NoteTemplateTarget, body: string): void;
  templates(rootId: string | null): Promise<TaskTemplate[]>;
  save(template: TaskTemplate): Promise<void>;
  remove(template: TaskTemplate): Promise<void>;
  canShare(rootId: string | null): boolean;
  rootName(rootId: string): string;
  confirmDelete(name: string): Promise<boolean>;
  date(): string;
  randomId(): string;
  toast(message: string, error?: boolean): void;
}

export function createNoteTemplateController(c: Capabilities) {
  const doc = c.document;
  let dialog: HTMLDialogElement | null = null;
  let returnFocus: HTMLElement | null = null;
  let focusKind: NoteTemplateTarget["kind"] = "quick";
  let preparing = false;
  const current = (target: NoteTemplateTarget) => {
    const next = c.read(target.kind);
    return next?.id === target.id && next.editable ? next : null;
  };
  const choices = async (target: NoteTemplateTarget): Promise<TemplateChoice[]> => [
    ...noteStarters.map(item => ({ key: `starter:${item.id}`, name: item.name, body: item.body, scope: "Starter" })),
    ...(await c.templates(target.rootId)).filter(item => item.purpose === "note" && !item.deleted && (!item.rootId || item.rootId === target.rootId)).map(record => ({ key: `${record.rootId || "personal"}:${record.id}`, name: record.name, body: record.description, scope: record.rootId ? "Space" : "Personal · this device", record })),
  ];

  function modal(label: string) {
    if (!dialog && !returnFocus) {
      const active = doc.activeElement as HTMLElement | null;
      returnFocus = active?.closest("details")?.querySelector<HTMLElement>("summary") || active;
    }
    dialog?.close();
    const element = doc.createElement("dialog"); element.className = "note-template-dialog"; element.setAttribute("aria-label", label);
    element.addEventListener("keydown", event => event.stopPropagation());
    element.addEventListener("close", () => { element.remove(); if (dialog === element) { dialog = null; const fallback = doc.querySelector<HTMLElement>(focusKind === "quick" ? ".quick-note-menu > summary" : ".reference-note-menu > summary"); (returnFocus?.isConnected ? returnFocus : fallback)?.focus(); returnFocus = null; } });
    doc.body.append(element); dialog = element; return element;
  }

  async function prepare(kind: NoteTemplateTarget["kind"]) {
    const before = c.read(kind);
    if (!before?.editable || !await c.flush(before)) return null;
    const after = current(before);
    return after ? { ...after, start: before.start, end: before.end } : null;
  }

  async function picker(target: NoteTemplateTarget) {
    focusKind = target.kind;
    let available = await choices(target);
    if (!current(target)) return;
    const element = modal("Note templates");
    element.innerHTML = `<h2>Use a note template</h2><form><label>Template<select name="template"></select></label><label>Title for {{title}}<input name="contextTitle" maxlength="200" value="${escapeHtml(target.title)}"></label><label>How to insert<select name="mode">${target.start !== undefined ? '<option value="insert">At cursor / selection</option>' : ""}<option value="append">Append to note</option><option value="replace" ${target.body ? "" : "selected"}>Replace note</option></select></label><small>Only {{title}} and {{date}} are expanded. Other text stays literal.</small><details open><summary>Result preview</summary><pre class="note-template-preview" data-template-preview></pre></details><label class="note-template-confirm" data-replace-warning hidden><input type="checkbox" name="replaceConfirmed">I want to replace the existing text shown in the preview.</label><p role="status" data-template-status></p><div class="note-template-management"><button type="button" class="text-button" data-template-edit hidden>Edit template</button><button type="button" class="text-button is-danger" data-template-delete hidden>Delete template</button></div><div class="dialog-actions"><button type="button" class="button button--soft" data-template-cancel>Cancel</button><button type="submit" class="button button--primary">Apply template</button></div></form>`;
    const select = element.querySelector<HTMLSelectElement>('[name="template"]')!;
    const searchLabel = doc.createElement("label"); searchLabel.className = "template-search-label"; searchLabel.append("Search note templates");
    const search = doc.createElement("input"); search.type = "search"; search.placeholder = "Search…"; search.autocomplete = "off"; searchLabel.append(search); select.closest("label")!.before(searchLabel);
    const title = element.querySelector<HTMLInputElement>('[name="contextTitle"]')!;
    const mode = element.querySelector<HTMLSelectElement>('[name="mode"]')!;
    const confirmation = element.querySelector<HTMLInputElement>('[name="replaceConfirmed"]')!;
    const warning = element.querySelector<HTMLElement>("[data-replace-warning]")!;
    const status = element.querySelector<HTMLElement>("[data-template-status]")!;
    const submit = element.querySelector<HTMLButtonElement>('[type="submit"]')!;
    const cancel = element.querySelector<HTMLButtonElement>("[data-template-cancel]")!;
    const edit = element.querySelector<HTMLButtonElement>("[data-template-edit]")!;
    const remove = element.querySelector<HTMLButtonElement>("[data-template-delete]")!;
    let busy = false;
    let applied: string | null = null;
    const date = c.date();
    const selected = () => available.find(item => item.key === select.value)!;
    const result = () => composeNoteTemplate(target.body, expandNoteTemplate(selected()?.body || "", title.value || selected()?.name || "", date), mode.value, target.start, target.end);
    function refresh() {
      const next = result();
      element.querySelector<HTMLElement>("[data-template-preview]")!.textContent = next;
      const replacing = mode.value === "replace" ? !!target.body.trim() : mode.value === "insert" && (target.end || 0) > (target.start || 0);
      warning.hidden = !replacing;
      submit.disabled = !selected() || replacing && !confirmation.checked;
      const record = selected()?.record;
      const manageable = !!record && (!record.rootId || c.canShare(record.rootId));
      edit.hidden = remove.hidden = !manageable;
    }
    function renderChoices() {
      const previous = select.value;
      const matches = available.filter(item => `${item.name} ${item.scope} ${item.body}`.toLocaleLowerCase().includes(search.value.trim().toLocaleLowerCase()));
      select.innerHTML = matches.map(item => `<option value="${escapeHtml(item.key)}">${escapeHtml(item.name)} · ${item.scope}</option>`).join("");
      if (matches.some(item => item.key === previous)) select.value = previous;
      status.textContent = matches.length ? "" : "No matching note templates.";
      confirmation.checked = false; refresh();
    }
    search.addEventListener("input", renderChoices);
    search.addEventListener("keydown", event => { if (event.key === "Enter") event.preventDefault(); });
    for (const control of [select, mode, title]) control.addEventListener("input", () => { confirmation.checked = false; refresh(); });
    confirmation.addEventListener("change", refresh);
    cancel.addEventListener("click", () => element.close());
    element.addEventListener("cancel", event => { if (busy) event.preventDefault(); });
    edit.addEventListener("click", () => { const record = selected().record; if (record) { element.close(); void editor(target, record).catch(() => c.toast("Could not open the template", true)); } });
    remove.addEventListener("click", async () => {
      const record = selected().record;
      if (!record || !await c.confirmDelete(record.name)) return;
      try {
        const latest = (await c.templates(target.rootId)).find(item => item.id === record.id && item.rootId === record.rootId);
        if (!latest || latest.deleted || latest.updatedAt !== record.updatedAt || (record.rootId && !c.canShare(record.rootId))) throw new Error("Template changed");
        await c.remove(latest); available = await choices(target); renderChoices(); c.toast("Note template deleted");
      } catch { status.textContent = "Could not delete the template. Access or content may have changed."; }
    });
    element.querySelector("form")!.addEventListener("submit", async event => {
      event.preventDefault(); if (busy || submit.disabled) return;
      busy = true; cancel.disabled = submit.disabled = true;
      try {
        const record = selected().record;
        if (record) {
          const latest = (await c.templates(target.rootId)).find(item => item.id === record.id && item.rootId === record.rootId && !item.deleted);
          if (!latest || latest.updatedAt !== record.updatedAt) throw new Error("The template changed. Reopen the preview to use its latest version.");
        }
        const latestTarget = current(target);
        if (!latestTarget || latestTarget.body !== (applied ?? target.body)) throw new Error("The note changed. Close this preview and reopen it before applying.");
        if (applied === null) { const body = result(); c.write(target, body); applied = body; }
        if (!await c.flush(target)) throw new Error("Save failed");
        element.close(); c.toast("Note template applied");
      } catch (error) {
        status.textContent = applied !== null ? "The note contains the applied text but could not be saved. Retry without inserting it again, or close to use normal note recovery." : error instanceof Error ? error.message : "Could not apply the template";
      } finally {
        busy = false; cancel.disabled = submit.disabled = false;
        if (applied !== null) { search.disabled = select.disabled = mode.disabled = confirmation.disabled = true; title.readOnly = true; edit.hidden = remove.hidden = true; }
        else refresh();
      }
    });
    renderChoices(); element.showModal();
  }

  async function editor(target: NoteTemplateTarget, existing?: TaskTemplate) {
    focusKind = target.kind;
    const element = modal(existing ? "Edit note template" : "Save note template");
    const id = existing?.id || c.randomId();
    element.innerHTML = `<h2>${existing ? "Edit note template" : "Save as note template"}</h2><form><label>Template name<input name="name" maxlength="120" required value="${escapeHtml(existing?.name || "")}"></label><label>Template text<textarea name="body" rows="7" maxlength="20000" required>${escapeHtml(existing?.description ?? target.body)}</textarea></label><small>Supports {{title}} and {{date}} only. Saving a template does not change this note.</small><label>Visibility<select name="scope" ${existing ? "disabled" : ""}><option value="personal">Personal · this device</option>${c.canShare(target.rootId) ? `<option value="space" ${existing?.rootId ? "selected" : ""}>Everyone in ${escapeHtml(c.rootName(target.rootId!))}</option>` : ""}</select></label><label class="note-template-confirm" data-share-warning hidden><input type="checkbox" name="shareConfirmed">I understand that everyone with access to this space can read this template text.</label><p role="status" data-template-status></p><div class="dialog-actions"><button type="button" class="button button--soft" data-template-cancel>Cancel</button><button type="submit" class="button button--primary">Save template</button></div></form>`;
    const scope = element.querySelector<HTMLSelectElement>("select")!;
    element.querySelector("textarea")!.setAttribute("aria-label", "Template text");
    if (existing?.rootId && !c.canShare(existing.rootId)) { element.close(); return; }
    const confirmation = element.querySelector<HTMLInputElement>('[name="shareConfirmed"]')!;
    const warning = element.querySelector<HTMLElement>("[data-share-warning]")!;
    const submit = element.querySelector<HTMLButtonElement>('[type="submit"]')!;
    const cancel = element.querySelector<HTMLButtonElement>("[data-template-cancel]")!;
    let busy = false;
    const refresh = () => { warning.hidden = scope.value !== "space"; submit.disabled = !warning.hidden && !confirmation.checked; };
    scope.addEventListener("change", () => { confirmation.checked = false; refresh(); }); confirmation.addEventListener("change", refresh);
    cancel.addEventListener("click", () => element.close());
    element.addEventListener("cancel", event => { if (busy) event.preventDefault(); });
    element.querySelector("form")!.addEventListener("submit", async event => {
      event.preventDefault(); if (busy || submit.disabled) return;
      busy = true; cancel.disabled = submit.disabled = true;
      try {
        if (!current(target)) throw new Error("Note access changed");
        const rootId = existing?.rootId ?? (scope.value === "space" ? target.rootId : "");
        const name = element.querySelector<HTMLInputElement>('[name="name"]')!.value;
        const body = element.querySelector<HTMLTextAreaElement>("textarea")!.value;
        if (rootId && !c.canShare(rootId)) throw new Error("Space editing access changed");
        if (existing) {
          const latest = (await c.templates(target.rootId)).find(item => item.id === existing.id && item.rootId === existing.rootId && !item.deleted);
          if (!latest || latest.updatedAt !== existing.updatedAt) throw new Error("The template changed. Reopen it before editing.");
        }
        const template = noteTemplateRecord(id, rootId || "", name, body, Date.now());
        if (existing) template.createdAt = existing.createdAt;
        await c.save(template); element.close(); c.toast(rootId ? "Note template saved locally and queued to sync" : "Personal note template saved on this device");
      } catch (error) { element.querySelector<HTMLElement>("[data-template-status]")!.textContent = error instanceof Error ? error.message : "Could not save the template"; }
      finally { busy = false; cancel.disabled = false; refresh(); }
    });
    refresh(); element.showModal();
  }

  doc.addEventListener("click", event => {
    const button = (event.target as Element | null)?.closest<HTMLElement>("[data-note-templates], [data-save-note-template]");
    if (!button || preparing || dialog?.open) return;
    const kind = button.dataset.noteTemplates || button.dataset.saveNoteTemplate;
    if (kind !== "quick" && kind !== "reference") return;
    event.preventDefault(); event.stopPropagation(); preparing = true;
    returnFocus = button.closest("details")?.querySelector<HTMLElement>("summary") || button;
    button.closest("details")?.removeAttribute("open");
    void (async () => {
      try {
        const target = await prepare(kind);
        if (!target) return;
        if (button.hasAttribute("data-save-note-template")) await editor(target); else await picker(target);
      } catch { c.toast("Could not open note templates", true); }
      finally { preparing = false; }
    })();
  }, true);
  return { picker, editor };
}
